import { ui } from "@patient-watch/ui";
import { DialogFooter } from "@patient-watch/ui/dialog";
import * as changeCase from "change-case";
import { formComponents as fc } from "@/features/i18n/formComponents";
import { useState } from "react";
import { ErrorBoundary } from "react-error-boundary";
import { cn } from "@/lib/utils";
import { EditPencil } from "@patient-watch/ui/edit-pencil";

import RequiredInfo from "./RequiredInfo";

interface Props {
    label?: string | React.ReactNode;
    /** Dialog header when different from the trigger (e.g. value-only trigger). */
    dialogTitle?: string | React.ReactNode;
    /** Supporting line under the dialog title. */
    dialogDescription?: string | React.ReactNode;
    /** Place the pen beside the label. Use when the trigger value is more than one line. */
    iconAlign?: "center" | "start";
    info: string | React.ReactNode;
    children?: React.ReactNode;
    locked?: boolean;
    description?: string;
    noInfo?: boolean;
    show?: boolean;
    help?: () => void;
    editable?: boolean;
    /** Use small, dense text when displaying as read-only (matches IconDetail style). */
    compact?: boolean;
    /** Show a Save button in the dialog footer that closes the dialog. */
    showSaveButton?: boolean;
    saveLabel?: string;
    dialogContentClassName?: string;
    triggerTestId?: string;
    dialogTestId?: string;
    /** Canonical stored value for locale-stable E2E assertions (e.g. "Left", "Knee"). */
    selectedValue?: string | null;
}

const EditDialog = ({
    label,
    dialogTitle,
    dialogDescription,
    iconAlign = "center",
    info,
    children,
    locked = false,
    description,
    noInfo = false,
    show = true,
    editable = true,
    compact = false,
    showSaveButton = false,
    saveLabel = "Save",
    dialogContentClassName,
    triggerTestId,
    dialogTestId,
    selectedValue,
}: Props) => {
    if (!show) return null;

    if (editable === false) {
        return <RequiredInfo label={label} info={info} description={description} noInfo={noInfo} compact={compact} />;
    }

    const [open, setOpen] = useState<boolean>(false);

    const triggerTestIdSlug =
        typeof label === "string"
            ? changeCase.kebabCase(label)
            : typeof label === "number"
              ? changeCase.kebabCase(String(label))
              : "edit-dialog-field";

    const openDialog = () => {
        if (locked !== true) {
            setOpen(true);
        } else {
            setOpen(false);
        }
    };

    const handleTriggerKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
        if (event.target !== event.currentTarget) return;
        if (event.key === "Enter" || event.key === " ") {
            event.preventDefault();
            openDialog();
        }
    };

    return (
        <ErrorBoundary fallback={<div>Error with {label}</div>}>
            <ui.Dialog open={open} onOpenChange={setOpen}>
                <div
                    onClick={openDialog}
                    onKeyDown={handleTriggerKeyDown}
                    role="button"
                    tabIndex={locked ? -1 : 0}
                    className={cn(
                        "flex justify-between m-0 w-full max-w-[94vw] overflow-clip",
                        iconAlign === "start" ? "items-start" : "items-center",
                        compact ? "gap-3 px-2 py-2.5 rounded-md transition-colors hover:bg-muted/50" : "p-0",
                        locked ? "cursor-text" : "cursor-pointer"
                    )}
                    data-testid={triggerTestId ?? `${triggerTestIdSlug}-trigger`}
                    data-selected-value={selectedValue ?? undefined}
                >
                    <RequiredInfo
                        label={label}
                        info={info}
                        description={description}
                        noInfo={noInfo}
                        compact={compact}
                    />
                    <EditPencil locked={locked} />
                </div>
                <ui.DialogContent className={cn("overflow-hidden", dialogContentClassName)} data-testid={dialogTestId}>
                    <ui.DialogHeader>
                        <ui.DialogTitle className="text-slate-900 dark:text-slate-100">
                            {dialogTitle ?? label}
                        </ui.DialogTitle>
                        {dialogDescription ? <ui.DialogDescription>{dialogDescription}</ui.DialogDescription> : null}
                    </ui.DialogHeader>
                    <div
                        className={cn(
                            "-mx-1 px-2 py-2 overflow-y-auto overscroll-contain",
                            showSaveButton
                                ? "max-h-[min(60vh,calc(95svh-12rem))]"
                                : "max-h-[min(70vh,calc(95svh-8rem))]"
                        )}
                    >
                        {children}
                    </div>
                    {showSaveButton ? (
                        <DialogFooter className="flex-col sm:flex-col gap-2">
                            <fc.SaveButton isDialog label={saveLabel} size="lg" />
                        </DialogFooter>
                    ) : null}
                </ui.DialogContent>
            </ui.Dialog>
        </ErrorBoundary>
    );
};

export default EditDialog;
